import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

/**
 * YieldHistoryChart
 *
 * Horizontal stacked bar chart showing yield per crop across seasons.
 * Pure SVG, no external charting library.
 */

const CROP_COLORS = [
  { bar: "#059669", bg: "#d1fae5" },   // emerald
  { bar: "#2563eb", bg: "#dbeafe" },   // blue
  { bar: "#d97706", bg: "#fef3c7" },   // amber
  { bar: "#7c3aed", bg: "#ede9fe" },   // violet
  { bar: "#dc2626", bg: "#fee2e2" },   // red
  { bar: "#0891b2", bg: "#cffafe" },   // cyan
];

const YieldHistoryChart = ({ yieldHistory, isArabic }) => {
  const [ref, inView] = useInView({ triggerOnce: true, rootMargin: "-40px" });

  if (!yieldHistory || yieldHistory.length === 0) return null;

  // Find max yield for scaling
  const maxYield = Math.max(...yieldHistory.map((s) => s.totalYield || 0), 1);

  return (
    <div ref={ref} className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm">
      <div className={`flex items-center justify-between mb-5 flex-wrap gap-3 ${isArabic ? "flex-row-reverse" : ""}`}>
        <div className={`flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
          <span className="text-lg">📊</span>
          <h3 className="text-base font-bold text-slate-900 mb-0">
            {isArabic ? "تاريخ الإنتاجية" : "Yield History"}
          </h3>
        </div>
        <span className="text-xs text-slate-400 font-medium">
          {isArabic ? "طن لكل موسم" : "Tons per season"}
        </span>
      </div>

      <div className="space-y-4">
        {yieldHistory.map((season, si) => (
          <div key={season.season} className="group">
            {/* Season label */}
            <div className={`flex items-center justify-between mb-2 ${isArabic ? "flex-row-reverse" : ""}`}>
              <span className="text-sm font-bold text-slate-700">{season.season}</span>
              <span className="text-sm font-mono font-bold text-emerald-600">
                {season.totalYield.toLocaleString()} t
              </span>
            </div>

            {/* Stacked bar */}
            <div className="h-8 w-full rounded-lg bg-slate-50 overflow-hidden flex">
              {season.crops.map((crop, ci) => {
                const width = (crop.yield / maxYield) * 100;
                const color = CROP_COLORS[ci % CROP_COLORS.length];
                return (
                  <motion.div
                    key={crop.name}
                    className="h-full relative group/bar"
                    style={{ backgroundColor: color.bar }}
                    initial={{ width: 0 }}
                    animate={inView ? { width: `${width}%` } : { width: 0 }}
                    transition={{ duration: 0.8, delay: si * 0.15 + ci * 0.1, ease: "easeOut" }}
                    title={`${crop.name}: ${crop.yield}t`}
                  >
                    {width > 8 && (
                      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white truncate px-1">
                        {crop.name}
                      </span>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Crop breakdown */}
            <div className={`flex flex-wrap gap-3 mt-1.5 ${isArabic ? "flex-row-reverse" : ""}`}>
              {season.crops.map((crop, ci) => {
                const color = CROP_COLORS[ci % CROP_COLORS.length];
                return (
                  <span key={crop.name} className={`flex items-center gap-1 text-[10px] text-slate-500 ${isArabic ? "flex-row-reverse" : ""}`}>
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color.bar }} />
                    {crop.name}: {crop.yield}t ({crop.area})
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default YieldHistoryChart;
